import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { isFeatureFlagEnabled } from '@ipfabric/ui-components/utils/featureFlags';
import {
  type Filters,
  type IntentRule,
  type OasDocument3XExtended,
  type SnapshotId,
  type TableColumnSort,
  createTableEndpointFromOas,
  isNonNullable,
} from '@ipfabric/utils';
import { ___, partialApply } from '@ipfabric/utils/fp/partialApply';
import { prop } from '@ipfabric/utils/fp/prop';
import { createConnectedApiTable } from 'components/ConnectedApiTable/ConnectedApiTable';
import { deviceExplorerTableCellContext } from 'components/DeviceView/DeviceViewContent/DeviceViewTable/deviceExplorerTableCellContext';
import type {
  DeviceViewEdgeEntity,
  DeviceViewEntityId,
  DeviceViewNodeEntity,
} from 'components/DeviceView/deviceViewTypes';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { option } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { type FC, memo, useEffect, useState } from 'react';
import { getOasOperationTable } from 'utils/oas/getOasOperationTable';
import { getRequestSnapshotId } from '../../helpers/getRequestSnapshotId';
import { isIntentVerificationAppliedForCurrentTable } from '../../helpers/isIntentVerificationAppliedForCurrentTable';
import { useDeviceViewContextSelectors } from '../../model/DeviceViewModel';
import { updateFilters } from '../../model/commands/updateFilters';
import { getFilters } from './filters/getFilters';

interface Props {
  active: boolean;
  deviceViewEntity: DeviceViewNodeEntity | DeviceViewEdgeEntity;
  deviceViewEntityId: DeviceViewEntityId;
  filters: Filters;
  intentRule: IntentRule | null;
  selectedTable: string;
  snapshotId: SnapshotId | null;
  snapshotToCompareId?: SnapshotId | null;
  sort?: TableColumnSort;
}

const tryToCreateApiTableFromOas = (
  oas: OasDocument3XExtended | null,
  apiTableEndpoint: string,
) => {
  if (oas === null) {
    return null;
  }
  return pipe(
    getOasOperationTable(oas, apiTableEndpoint),
    option.fromNullable,
    option.map(
      partialApply(
        createTableEndpointFromOas,
        apiTableEndpoint,
        ___,
        isFeatureFlagEnabled('ENABLE_NEW_CLOUD_MODEL'),
      ),
    ),
    option.map((tableEndpoint) => createConnectedApiTable({ tableEndpoint })),
    option.toNullable,
  );
};

const BareDeviceViewTable: FC<Props> = (props) => {
  const ctxProps = useDeviceViewContextSelectors({
    dispatch: prop('dispatch'),
    routesWithApiEndpoint: prop('routesWithApiEndpoint'),
  });
  const globalStateCtxProps = useGlobalStateContextSelectors({
    oas: prop('oas'),
  });
  const [visited, setVisited] = useState<boolean>(false);

  const table = ctxProps.routesWithApiEndpoint[props.selectedTable];

  // biome-ignore lint/correctness/useExhaustiveDependencies(visited): to be triggered only by activation
  useEffect(() => {
    if (!visited && props.active) {
      setVisited(true);
    }
  }, [props.active]);

  const handleFilterChange = (filters: Filters) => {
    ctxProps.dispatch(updateFilters, {
      deviceViewEntityId: props.deviceViewEntityId,
      tableId: props.selectedTable,
      filters: filters || {},
    });
  };

  if (!visited) {
    return null;
  }

  if (table === undefined) {
    stumble('Invalid Device Explorer table');
    return null;
  }

  const { apiEndpoint, tableComponent } = table;
  const ApiTableComponent =
    tableComponent ??
    // Fallback to create table component from apiEndpoint if it was not found in the routes
    tryToCreateApiTableFromOas(globalStateCtxProps.oas, table.apiEndpoint) ??
    undefined;

  if (ApiTableComponent !== undefined) {
    return (
      <ApiTableComponent
        borderless
        className='flex-1'
        enableTableReports={props.intentRule !== null}
        disableViewUrl
        initialFilters={props.filters}
        initialSort={props.sort}
        onFiltersSubmit={handleFilterChange}
        showOnlyIntentResultsOnColumns={
          isIntentVerificationAppliedForCurrentTable(
            props.selectedTable,
            props.intentRule,
          ) && isNonNullable(props.intentRule?.column)
            ? [props.intentRule.column]
            : undefined
        }
        snapshotId={getRequestSnapshotId(
          props.deviceViewEntity,
          props.snapshotId,
          props.snapshotToCompareId,
        )}
        staticFilters={getFilters(
          apiEndpoint,
          props.deviceViewEntity,
          props.selectedTable,
          props.intentRule,
          globalStateCtxProps.oas!,
        )}
        tableCellContext={deviceExplorerTableCellContext}
        temporarySettings
        disableTableRowDetail
      />
    );
  }

  stumble(`tableComponent was not found for api endpoint ${apiEndpoint}`);
  return null;
};

export const DeviceViewTable = memo(BareDeviceViewTable);
